import { PayloadAction, createSlice, current } from '@reduxjs/toolkit';
import type {
    IWebsocketChannelClosePayload,
    IWebsocketChannelOpenPayload,
    IWebsocketChannelSendPayload,
    IWebsocketChannels,
    IWebsocketClosePayload,
    IWebsocketErrorPayload,
    IWebsocketMessagePayload,
    IWebsocketOpenPayload,
    IWebsocketReadyStatePayload,
    IWebsocketState,
    IWebsocketSubscriptionById,
    IWebsocketSubscriptionByIdState,
    IWebsocketTradingViewSubscribeActionPayload,
} from './types/websocketSubscribersReducer.t';
import { messageToObject } from './helpers';
import { LOGOUT_SUCCESS } from '../../modules/Login/loginConstants';

const initialState: IWebsocketState = {
    inited: null,
    readyState: {},
    channels: {},
    subscriptionsById: {},
    messages: {},
    messagesByID: {},
};

const websocketSlice = createSlice({
    name: 'websocket',
    initialState,
    reducers: {
        websocketOpenSubscriptionById: (
            state: IWebsocketState,
            action: PayloadAction<IWebsocketSubscriptionById>,
        ) => {},
        websocketSaveSubscriptionById: (state: IWebsocketState, action: PayloadAction<IWebsocketSubscriptionById>) => {
            const { channel, id } = action.payload;
            if (!state.subscriptionsById[channel]) {
                state.subscriptionsById[channel] = {};
            }
            state.subscriptionsById[channel][id] = action.payload;
        },
        websocketSetSubscriptionByIdState: (
            state: IWebsocketState,
            action: PayloadAction<IWebsocketSubscriptionByIdState>,
        ) => {
            const { channel, id, state: subscriptionsState } = action.payload;
            if (!state.subscriptionsById[channel]) {
                state.subscriptionsById[channel] = {};
            }
            if (Boolean(state.subscriptionsById[channel][id])) {
                state.subscriptionsById[channel][id].state = subscriptionsState;
            }
        },
        websocketCloseSubscriptionById: (
            state: IWebsocketState,
            action: PayloadAction<{ id: string; channel: string }>,
        ) => {},
        websocketClearSubscriptionById: (
            state: IWebsocketState,
            action: PayloadAction<{ id: string; channel: string }>,
        ) => {
            const { channel, id } = action.payload;
            if (state.subscriptionsById[channel] && state.subscriptionsById[channel][id]) {
                delete state.subscriptionsById[channel][id];
            }
        },
        websocketTradingViewSubscribeAction: (
            state: IWebsocketState,
            action: PayloadAction<IWebsocketTradingViewSubscribeActionPayload>,
        ) => {},
        websocketTradingViewUnsubscribeAction: (state: IWebsocketState, action: PayloadAction<{ id: string }>) => {},

        // channel

        websocketChannelOpen: (state: IWebsocketState, action: PayloadAction<IWebsocketChannelOpenPayload>) => {},

        websocketChannelClose: (state: IWebsocketState, action: PayloadAction<IWebsocketChannelClosePayload>) => {},

        websocketChannelSend: (state: IWebsocketState, action: PayloadAction<IWebsocketChannelSendPayload>) => {},

        websocketChannelSended: (state: IWebsocketState, action: PayloadAction<IWebsocketChannelSendPayload>) => {},

        websocketChannelUpdated: (state: IWebsocketState, action: PayloadAction<IWebsocketChannels>) => {
            state.channels = action.payload;
        },
        
        websocketChannelReadyStateChange: (state: IWebsocketState, action: PayloadAction<IWebsocketReadyStatePayload>) => {
            state.readyState = {};
            const result = Object.entries(state.channels).map(([channel, { readyState }]) => {
                state.readyState[channel] = readyState;
                return readyState;
            });
            const allOpen = result.every((item) => item === WebSocket.OPEN)
            if (state.inited === null && allOpen) {
                state.inited = true;
            }
        },
        
        // event
        websocketEventMessage: (state: IWebsocketState, action: PayloadAction<IWebsocketMessagePayload>) => {
            const { channel, event } = action.payload;
            const message = messageToObject(event) as { inReplyTo?: string };
            const currentState = current(state);
            if (message) {
                if (currentState.messages[channel]) {
                    const list = [...currentState.messages[channel], message];
                    list.splice(0, 100);
                    state.messages[channel] = list;
                } else {
                    state.messages[channel] = [message];
                }
                if (message.inReplyTo) {
                    let channelList = {};
                    if (currentState.messagesByID[channel]) {
                        channelList = { ...currentState.messagesByID[channel] };
                    }
                    channelList[message.inReplyTo] = message;
                    state.messagesByID[channel] = channelList;
                }
            }
        },

        websocketEventOpen: (state: IWebsocketState, action: PayloadAction<IWebsocketOpenPayload>) => {},

        websocketEventClose: (state: IWebsocketState, action: PayloadAction<IWebsocketClosePayload>) => {},

        websocketEventError: (state: IWebsocketState, action: PayloadAction<IWebsocketErrorPayload>) => {},
    },
    extraReducers: (builder) => {
        builder.addCase(LOGOUT_SUCCESS, (state) => {
            state.inited = null;  
        })
    },
});

export const {
    websocketClearSubscriptionById,
    websocketCloseSubscriptionById,
    websocketOpenSubscriptionById,
    websocketSaveSubscriptionById,
    websocketTradingViewSubscribeAction,
    websocketTradingViewUnsubscribeAction,
    websocketSetSubscriptionByIdState,
    // channel
    websocketChannelClose,
    websocketChannelOpen,
    websocketChannelSend,
    websocketChannelSended,
    websocketChannelUpdated,
    websocketChannelReadyStateChange,
    // event
    websocketEventClose,
    websocketEventError,
    websocketEventMessage,
    websocketEventOpen,
} = websocketSlice.actions;
const websocketReducer = websocketSlice.reducer;
export default websocketReducer;
